/**
 * F-126 stem slots — one chip per mood run, pinned over the TOP cell of the
 * run in the BGM column. Since F-139 a slot holds a LIST of stems and reads
 * as covered only with `BGM_MIN_STEMS` of them: the chip shows `n/2` and the
 * titles, and every way in (click, drop) ADDS a stem rather than replacing.
 *
 * A LAYER, not a cell renderer. The grid's `CellView` is a memoized text node
 * on the hot path of every keystroke, scroll and zoom (docs/09), and threading
 * an audio player through it would put a `<audio>` element and its state into
 * that path for every board, scored or not. Rendering here instead — inside
 * `CameraLayer`, in raw board units off the same `GridLayoutSnapshot` the
 * sections bands use — keeps the grid untouched and the chips glued to their
 * cells through pan, zoom, sort and F-26 auto-grow.
 *
 * Unlike the sections and design bands this layer is NOT pointer-transparent:
 * a stem slot exists to be clicked. It is deliberately small and exactly cell
 * sized, so the only pixels that stop panning are the ones that do something.
 *
 * The cell underneath still holds the stems' TITLES as ordinary text (that is
 * `cellPlainText`), so the score survives CSV export and reads correctly for
 * anyone on an older client that does not render this layer.
 *
 * Two things added 2026-09-03:
 *  - each chip is a DROP TARGET: audio files dragged from the desktop onto it
 *    upload and join the run's stems, no dialog. The listeners are native and
 *    stop propagation AT the chip, because the canvas's image and script drop
 *    layers listen on the container underneath and would otherwise handle the
 *    same drop (the script one calling it "not a script file"); React's
 *    delegated handlers would run after them, too late.
 *  - a divided sheet with no BGM column — a board divided before music slots
 *    existed — gets one added here, automatically, once the doc has synced
 *    (ensure-column.ts): deterministic id, so two people opening that board
 *    at once converge on ONE column, and off the undo stack.
 */
import {
  BGM_MIN_STEMS,
  bgmColumns,
  bgmStemsOf,
  boardBgmCoverage,
  type BgmCellValue,
} from "@kola/shared";
import { useEffect, useRef, useState } from "react";
import { useStore } from "zustand";

import { uploadAudioAsset } from "../../core/api.js";
import { openExternalUrl } from "../../core/host.js";
import type { BoardSession } from "../../core/session.js";
import { showToast } from "../../ui/toast.js";
import type { GridLayoutSnapshot } from "../board/GridTable.js";
import { planBgmColumn } from "./ensure-column.js";
import { bgmSlots, type BgmSlot } from "./layout.js";
import {
  dragHasAudio,
  isAudioFile,
  stemLinkUrl,
  stemPlaybackUrl,
  titleFromFileName,
} from "./stem.js";
import { useDocVersion } from "../board/useDocVersion.js";
import { bgmUi } from "./store.js";

export interface BgmSlotLayerProps {
  session: BoardSession;
  /** Latest grid layout pass — positions the slots. May be null. */
  layout: GridLayoutSnapshot | null;
}

export function BgmSlotLayer({ session, layout }: BgmSlotLayerProps) {
  const editing = useStore(bgmUi, (s) => s.editing);
  const synced = useStore(session.store, (s) => s.synced);
  // The doc is not reactive on its own; re-derive whenever it changes, the
  // same way the design band subscribes. Cheap: a walk of one column.
  const version = useDocVersion(session);
  const [playing, setPlaying] = useState<string | null>(null);
  /** Rows whose drop-upload is in flight. */
  const [uploading, setUploading] = useState<ReadonlySet<string>>(() => new Set());

  // Boards divided before F-126: add the missing BGM column, once per sheet
  // per mount. Only after sync — before it the doc is empty and every sheet
  // looks undivided; and only when the add actually happened, so a frozen
  // (offline) session tries again once it is back.
  const sheetId = layout?.sheetId ?? null;
  const ensured = useRef<string | null>(null);
  useEffect(() => {
    if (sheetId === null || !synced || ensured.current === sheetId) return;
    const plan = planBgmColumn(session.board, sheetId);
    if (plan === null) return;
    if (session.addSystemColumn(sheetId, plan.colId, plan)) {
      ensured.current = sheetId;
      showToast("Added a BGM column — one music slot per mood");
    }
    // `version` is what re-runs this when the doc changes (a Mood column
    // appearing later, or arriving with the initial sync).
  }, [session, sheetId, synced, version]);

  if (layout === null) return null;

  const { bgmColId } = bgmColumns(session.board, layout.sheetId);
  if (bgmColId === undefined) return null;
  const coverage = boardBgmCoverage(session.board, layout.sheetId);
  const slots = bgmSlots(coverage, bgmColId, layout);
  if (slots.length === 0) return null;

  /** Files dropped on a slot: upload each, then ADD it — same doc write as the dialog. */
  const dropStems = async (slot: BgmSlot, files: readonly File[]): Promise<void> => {
    const audio = files.filter(isAudioFile);
    if (audio.length === 0) {
      showToast("Drop an mp3, wav, m4a, aac, ogg or flac file");
      return;
    }
    if (session.store.getState().frozen) {
      showToast("The board is read-only while offline — the music was not added");
      return;
    }
    // Captured NOW (docs/07 C4): the async upload must not re-resolve
    // against moved state.
    const target = { sheetId: layout.sheetId, rowId: slot.rowId, colId: bgmColId };
    setUploading((prev) => new Set(prev).add(slot.rowId));
    try {
      const added: string[] = [];
      for (const file of audio) {
        const result = await uploadAudioAsset(session.boardId, file);
        const title = titleFromFileName(file.name);
        if (
          !session.board.hasRow(target.sheetId, target.rowId) ||
          !session.board.hasColumn(target.sheetId, target.colId)
        ) {
          showToast("That mood's slot was deleted while the file uploaded");
          return;
        }
        let wrote = false;
        session.batch(() => {
          session.board.addCellBgmStem(target.sheetId, target.rowId, target.colId, {
            source: "asset",
            assetId: result.assetId,
            title,
          });
          wrote = true;
        });
        if (!wrote) {
          showToast("Offline — the music wasn't saved");
          return;
        }
        added.push(title);
      }
      const now = session.board.getCellBgm(target.sheetId, target.rowId, target.colId);
      const count = now === undefined ? 0 : bgmStemsOf(now).length;
      showToast(
        `${added.map((title) => `"${title}"`).join(", ")} added for ${slot.title || "this mood"} — ${count} of ${BGM_MIN_STEMS} stems`,
      );
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Upload failed. Try again.");
    } finally {
      setUploading((prev) => {
        const next = new Set(prev);
        next.delete(slot.rowId);
        return next;
      });
    }
  };

  return (
    <>
      {slots.map((slot) => (
        <BgmSlotChip
          key={slot.rowId}
          slot={slot}
          stem={session.board.getCellBgm(layout.sheetId, slot.rowId, bgmColId)}
          isEditing={editing !== null && editing.rowId === slot.rowId && editing.colId === bgmColId}
          playing={playing === slot.rowId}
          uploading={uploading.has(slot.rowId)}
          onTogglePlay={() => setPlaying((id) => (id === slot.rowId ? null : slot.rowId))}
          onStopped={() => setPlaying(null)}
          onEdit={() =>
            bgmUi.getState().edit({
              sheetId: layout.sheetId,
              rowId: slot.rowId,
              colId: bgmColId,
              label: slot.title,
            })
          }
          onDrop={(files) => void dropStems(slot, files)}
        />
      ))}
    </>
  );
}

interface BgmSlotChipProps {
  slot: BgmSlot;
  stem: BgmCellValue | undefined;
  isEditing: boolean;
  playing: boolean;
  uploading: boolean;
  onTogglePlay(): void;
  onStopped(): void;
  onEdit(): void;
  onDrop(files: readonly File[]): void;
}

function BgmSlotChip({
  slot,
  stem,
  isEditing,
  playing,
  uploading,
  onTogglePlay,
  onStopped,
  onEdit,
  onDrop,
}: BgmSlotChipProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [over, setOver] = useState(false);
  // Set when <audio> refuses the source; cleared whenever the stem changes, so
  // replacing a broken link with a good one restores the preview button.
  const [playFailed, setPlayFailed] = useState(false);
  // The chip previews the FIRST stem of the slot's list (F-139); the dialog
  // previews each one.
  const stems = stem === undefined ? [] : bgmStemsOf(stem);
  const first = stems[0];
  const stemKey = first === undefined ? "" : `${first.source}:${first.assetId ?? first.url ?? ""}`;
  useEffect(() => setPlayFailed(false), [stemKey]);
  // The latest handler, read at drop time: the listeners below are attached
  // once, and re-attaching them on every render would be churn for nothing.
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;

  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const onDragOver = (event: DragEvent) => {
      if (!dragHasAudio(event)) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
      setOver(true);
    };
    const onDragLeave = (event: DragEvent) => {
      const related = event.relatedTarget as Node | null;
      if (related === null || !el.contains(related)) setOver(false);
    };
    const onDropEvent = (event: DragEvent) => {
      if (!dragHasAudio(event)) return;
      event.preventDefault();
      event.stopPropagation();
      setOver(false);
      onDropRef.current([...(event.dataTransfer?.files ?? [])]);
    };
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDropEvent);
    return () => {
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDropEvent);
    };
  }, []);

  // Two different affordances, never both: a stem Kola can play gets ▶, and a
  // stem that is a LINK to a page (a YouTube video, a Spotify track — most of
  // what actually gets pasted) gets ↗ and opens it. `playFailed` moves a stem
  // from the first group to the second at runtime, when a link that looked
  // like a file turns out not to load.
  const url = first === undefined || playFailed ? null : stemPlaybackUrl(first);
  const link = first === undefined ? null : stemLinkUrl(first);
  const titles = stems.map((s) => s.title).join(" · ");
  const label = uploading
    ? "Uploading…"
    : stems.length === 0
      ? `＋ Add music (${BGM_MIN_STEMS} stems)`
      : titles;
  const tip = uploading
    ? "Uploading…"
    : stems.length === 0
      ? `Add ${BGM_MIN_STEMS} stems for "${slot.title}" — click, or drop audio files here`
      : `${stems.length} of ${BGM_MIN_STEMS} stems: ${titles} — click to add or remove, or drop another file here`;

  return (
    <div
      ref={ref}
      className={
        "bgm-slot" +
        (slot.covered
          ? " bgm-slot-filled"
          : stems.length > 0
            ? " bgm-slot-partial"
            : " bgm-slot-empty") +
        (isEditing ? " bgm-slot-editing" : "") +
        (over ? " bgm-slot-over" : "") +
        (uploading ? " bgm-slot-uploading" : "")
      }
      style={{ left: slot.x, top: slot.y, width: slot.w, height: slot.h }}
      // The chip owns its clicks; the rest of the canvas keeps panning.
      onPointerDown={(event) => event.stopPropagation()}
    >
      {first !== undefined && url !== null && !uploading && (
        <button
          type="button"
          className="bgm-slot-play"
          aria-label={playing ? "Stop preview" : `Preview ${first.title}`}
          data-tip={playing ? "Stop" : `Preview ${first.title}`}
          onClick={onTogglePlay}
        >
          {playing ? "■" : "▶"}
        </button>
      )}
      {/* The link this stem actually is, when Kola cannot play it. Through
          `openExternalUrl`, not an <a href>: inside the desktop shell a link
          must leave for the OS browser rather than navigate the app's own
          window away from the board. */}
      {first !== undefined && url === null && link !== null && !uploading && (
        <button
          type="button"
          className="bgm-slot-play bgm-slot-open"
          aria-label={`Open ${first.title} in your browser`}
          data-tip="Open the link"
          onClick={() => openExternalUrl(link)}
        >
          ↗
        </button>
      )}
      <button
        type="button"
        className="bgm-slot-label"
        disabled={uploading}
        data-tip={tip}
        onClick={onEdit}
      >
        {label}
      </button>
      {/* F-139: how far the slot is from reviewable — "1/2" says exactly what
          is still missing, where a bare dashed outline would not. */}
      {stems.length > 0 && !uploading && (
        <span className="bgm-slot-count" aria-label={`${stems.length} of ${BGM_MIN_STEMS} stems`}>
          {slot.covered ? `✓ ${stems.length}` : `${stems.length}/${BGM_MIN_STEMS}`}
        </span>
      )}
      {/* A stem sitting below its run's top cell is INVISIBLE to the coverage
          count, which only ever looks at the top. Saying so beats a bare
          "missing" on a run that plainly has music in it. */}
      {!slot.covered && slot.misplacedStem && (
        <span
          className="bgm-slot-warn"
          data-tip="A stem in this mood is not on its first row — only the first row counts"
        >
          !
        </span>
      )}
      {playing && url !== null && (
        <audio
          src={url}
          autoPlay
          onEnded={onStopped}
          // A link that ended in .mp3 but does not load (a private CDN, a 404,
          // a codec this browser lacks) used to flick back to ▶ and leave the
          // designer with no way to reach it. Remember the failure: the chip
          // swaps to the ↗ that opens the link, for a linked stem.
          onError={() => {
            onStopped();
            const what = first?.title ?? "That track";
            if (link !== null) {
              setPlayFailed(true);
              showToast(`"${what}" can't be played here — open the link instead`);
            } else {
              showToast(`"${what}" could not be played`);
            }
          }}
        />
      )}
    </div>
  );
}

